import * as L from 'leaflet';
import { pointInRing } from './ring-geometry';
export { pointInRing };

/**
 * Pulls a dragged or placed point onto a nearby boundary.
 *
 * This is the difference between adjacent territories being possible and being
 * a chore. Patches are not allowed to overlap, and two drawn freehand never
 * share an edge - they always cross by a metre somewhere, so every save reports
 * a repair and quietly moves somebody's border. Snapping makes the shared edge
 * exactly shared, which the database reads as touching rather than overlapping,
 * and the repair never fires.
 *
 * Distances are measured in SCREEN pixels, not degrees. A tolerance in degrees
 * would be a hair at street zoom and half a county at city zoom; in pixels the
 * pull feels the same wherever the user is.
 *
 * Two levels of it live here. `snapPoint` pulls ONE point onto a boundary, which
 * is what a dragged handle needs. `conflateRing` uses that to make a whole
 * freehand ring share its neighbours' EDGES rather than merely touch their
 * corners - see its own note for why per-point snapping alone still leaves
 * slivers.
 */

export type SnapKind = 'vertex' | 'edge';

export interface SnapResult {
  readonly latlng: L.LatLng;
  readonly kind: SnapKind;
  /** Which of the `rings` it landed on. */
  readonly ring: number;
  /**
   * Where along that ring, as a fractional vertex index: `3` is exactly vertex
   * 3, `3.25` is a quarter of the way from vertex 3 to vertex 4. This is what
   * lets two snapped points name a RUN of the neighbour's boundary rather than
   * just two isolated positions on it - see `conflateRing`.
   */
  readonly at: number;
}

/** How close the cursor has to be, in pixels, before the point is taken. */
export const SnapRadiusPx = 12;

/**
 * Nearer to a boundary than this, in pixels, and a point counts as being on it
 * rather than across it. Sub-pixel on purpose: this is not a tolerance for
 * "close enough to share", which is SnapRadiusPx's job, but the width of the
 * line itself, so that a corner snapping produced is not then read as an
 * overlap and pushed back out of the seam it was just put into.
 */
const OnBoundaryPx = 0.5;

/**
 * The nearest snappable point on any of `rings`, or null when nothing is close.
 *
 * A vertex beats an edge even when the edge is marginally nearer: corners are
 * what people mean to meet, and landing one pixel along the edge from a corner
 * leaves a sliver that the overlap repair will later report.
 */
export function snapPoint(
  point: L.LatLng,
  map: L.Map,
  rings: readonly (readonly L.LatLng[])[],
  radiusPx: number = SnapRadiusPx,
): SnapResult | null {
  if (!rings.length) return null;

  const origin = map.latLngToLayerPoint(point);
  let bestVertex: { latlng: L.LatLng; ring: number; at: number; d: number } | null = null;
  let bestEdge: { latlng: L.LatLng; ring: number; at: number; d: number } | null = null;

  for (let r = 0; r < rings.length; r++) {
    const ring = rings[r];
    for (let i = 0; i < ring.length; i++) {
      const a = map.latLngToLayerPoint(ring[i]);
      const dv = origin.distanceTo(a);
      if (dv <= radiusPx && (bestVertex === null || dv < bestVertex.d)) {
        bestVertex = { latlng: ring[i], ring: r, at: i, d: dv };
      }

      // Closing segment included: a ring's last-to-first edge is as snappable
      // as any other, and it is the one people miss when it is not.
      const b = map.latLngToLayerPoint(ring[(i + 1) % ring.length]);
      const foot = closestPointOnSegment(origin, a, b);
      const de = origin.distanceTo(foot.point);
      if (de <= radiusPx && (bestEdge === null || de < bestEdge.d)) {
        bestEdge = {
          latlng: map.layerPointToLatLng(foot.point),
          ring: r,
          at: i + foot.t,
          d: de,
        };
      }
    }
  }

  const best = bestVertex ?? bestEdge;
  if (!best) return null;
  return {
    latlng: best.latlng,
    kind: best === bestVertex ? 'vertex' : 'edge',
    ring: best.ring,
    at: best.at,
  };
}

/** Perpendicular foot of `p` on segment `a`-`b`, clamped to the segment, with
 *  how far along the segment it fell. */
function closestPointOnSegment(p: L.Point, a: L.Point, b: L.Point): { point: L.Point; t: number } {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lengthSq = dx * dx + dy * dy;
  // A degenerate segment - two identical vertices - has no direction to project
  // onto, and its own endpoint is the honest answer.
  if (lengthSq === 0) return { point: a, t: 0 };

  const t = Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / lengthSq));
  return { point: L.point(a.x + t * dx, a.y + t * dy), t };
}

/**
 * The nearest point on `ring`'s outline, measured on screen.
 *
 * Pixels rather than degrees for the same reason snapping uses them: a metre of
 * longitude is not a metre of latitude, and "nearest" should mean what it looks
 * like on the map.
 */
function closestOnRing(point: L.LatLng, map: L.Map, ring: readonly L.LatLng[]):
    { latlng: L.LatLng; d: number } | null {
  if (ring.length < 2) return null;
  const origin = map.latLngToLayerPoint(point);
  let best: { latlng: L.LatLng; d: number } | null = null;
  for (let i = 0; i < ring.length; i++) {
    const a = map.latLngToLayerPoint(ring[i]);
    const b = map.latLngToLayerPoint(ring[(i + 1) % ring.length]);
    const foot = closestPointOnSegment(origin, a, b).point;
    const d = origin.distanceTo(foot);
    if (best === null || d < best.d) best = { latlng: map.layerPointToLatLng(foot), d };
  }
  return best;
}

/**
 * Pushes a point that has landed INSIDE a neighbour back onto that neighbour's
 * edge, or null when it is not inside anything.
 *
 * This is the half of snapping that proximity cannot do, and the reason it is
 * needed is arithmetic: snapping fires within twelve pixels of a boundary, so a
 * corner dropped well inside a neighbouring patch is near nothing and is left
 * exactly where it fell. The shapes then overlap, which is the one thing
 * territories may not do, and the server repairs it at save time by moving
 * somebody's border. Being inside is not a matter of degree, so this has no
 * tolerance: any distance in is too far, and the way out is the shortest one.
 */
export function pullOutOf(
  point: L.LatLng,
  map: L.Map,
  targets: readonly (readonly L.LatLng[])[],
): L.LatLng | null {
  let best: { latlng: L.LatLng; d: number } | null = null;
  for (const ring of targets) {
    if (!pointInRing(point, ring)) continue;
    const out = closestOnRing(point, map, ring);
    if (!out) continue;
    // Already ON the border rather than past it. That is a shared corner - the
    // thing snapping works to produce - so it is left alone and not counted as
    // an overlap. Without this the tool undoes its own seams, and reports every
    // one of them as a correction it had to make.
    if (out.d <= OnBoundaryPx) continue;
    // Overlapping neighbours should not exist, but if two ever do, leaving by
    // the nearest edge is still the right answer for the one being dragged.
    if (best === null || out.d < best.d) best = out;
  }
  return best?.latlng ?? null;
}

/** What `pullOutOfOverlaps` had to correct. */
export interface OverlapFix {
  readonly ring: L.LatLng[];
  /** How many corners had landed inside a neighbour. */
  readonly pulled: number;
}

/**
 * Every corner of `ring` that landed inside a neighbour, put back on that
 * neighbour's edge.
 *
 * Corner-level, and deliberately so: an edge can still cross a neighbour with
 * both its ends outside, and answering that properly is polygon clipping, which
 * is the server's job and is where the authoritative repair already lives. What
 * this removes is the overlap a person can see themselves having made - a patch
 * drawn straight over its neighbour - which is the case that was reaching the
 * server. The repair remains the backstop; it just stops being the first line.
 */
export function pullOutOfOverlaps(
  ring: readonly L.LatLng[],
  map: L.Map,
  targets: readonly (readonly L.LatLng[])[],
): OverlapFix {
  if (!targets.length) return { ring: [...ring], pulled: 0 };
  let pulled = 0;
  const out = ring.map(p => {
    const moved = pullOutOf(p, map, targets);
    if (moved) pulled++;
    return moved ?? p;
  });
  return { ring: dropAdjacentDuplicates(out), pulled };
}

/**
 * How wide a gap, in screen pixels, still counts as two people meaning the same
 * border.
 *
 * Wider than `SnapRadiusPx` on purpose, and the difference is what the two
 * numbers are for. `SnapRadiusPx` is a LIVE pull: it fires under a moving
 * cursor, so it has to stay tight or the line jumps to a border the user was
 * only passing near. This one is applied once, to a finished stroke, where the
 * whole shape is visible and the question is no longer "what is the user aiming
 * at" but "did they leave a strip of ground belonging to nobody". A strip this
 * thin is never deliberate - it is the width of a hand tremor - and leaving it
 * costs an admin a sliver of unassigned doors they cannot see to fix.
 */
export const GapTolerancePx = 24;

/** What `absorbGaps` had to close. */
export interface GapFix {
  readonly ring: L.LatLng[];
  /** How many corners were sitting in a gap and got put on the border. */
  readonly closed: number;
}

/**
 * Pulls corners that stopped just SHORT of a neighbour onto it, so the sliver of
 * no-man's-land between the two shapes is absorbed by the one being drawn.
 *
 * The mirror of `pullOutOfOverlaps`, and needed for the same reason. That one
 * handles a corner that went too far and landed inside a neighbour; this one
 * handles the opposite miss, which is both more common and quieter: the stroke
 * lands a few pixels outside, nothing overlaps, no repair fires, and the map
 * looks right. What is actually there is a ribbon of ground in no territory at
 * all, and every door inside it is unassigned - which shows up much later as a
 * rep whose book is short and no visible reason why.
 *
 * Corners already inside a neighbour are left alone rather than pulled to the
 * near edge: that is an overlap, `pullOutOfOverlaps` owns it, and moving them
 * here would undo its work in the same pass. Corners already ON the border are
 * left alone too - there is no gap to close, and rewriting the coordinate would
 * only cost the seam its exactness.
 */
export function absorbGaps(
  ring: readonly L.LatLng[],
  map: L.Map,
  targets: readonly (readonly L.LatLng[])[],
  tolerancePx: number = GapTolerancePx,
): GapFix {
  if (!targets.length) return { ring: [...ring], closed: 0 };

  let closed = 0;
  const out = ring.map(p => {
    if (targets.some(t => pointInRing(p, t))) return p;

    const found = snapPoint(p, map, targets, tolerancePx);
    if (!found) return p;

    // Already on the line: a seam, not a gap.
    const from = map.latLngToLayerPoint(p);
    const to = map.latLngToLayerPoint(found.latlng);
    if (from.distanceTo(to) <= OnBoundaryPx) return p;

    closed++;
    return found.latlng;
  });

  return { ring: dropAdjacentDuplicates(out), closed };
}

/** What `conflateRing` made of a ring. */
export interface ConflateResult {
  /** The ring to use, with any shared runs replaced by the neighbour's own
   *  vertices. Never shorter than the input's snapped form. */
  readonly ring: L.LatLng[];
  /** How many vertices were adopted from a neighbour. Zero means nothing was
   *  near enough to share, which is worth saying out loud rather than leaving
   *  the user to find out at save time. */
  readonly adopted: number;
  /** How many separate runs those vertices came in - roughly, how many
   *  neighbours this shape now seams onto. */
  readonly seams: number;
}

/**
 * Makes a freehand ring share its neighbours' boundaries EXACTLY, not merely
 * closely.
 *
 * Snapping each vertex on its own is not enough, and this is the whole reason
 * this function exists. Two vertices land on the neighbour's line; the edge
 * BETWEEN them is still the straight line the pointer drew, so wherever the
 * neighbour bends in between, the two shapes cross. That is a sliver, the
 * database reads it as an overlap, and the save reports a repair that moves
 * somebody's border. The fix is to stop treating the neighbour as a set of
 * points to land on and start treating it as a path to walk: where two
 * consecutive vertices snapped onto the same ring, the neighbour's own vertices
 * between them are taken verbatim, so the seam is byte-for-byte identical on
 * both sides and no repair can fire.
 *
 * The direction is chosen by walking both ways round the neighbour and keeping
 * the shorter path, then checking that the drawn edge's midpoint really does lie
 * near that ring. Without the midpoint check, a shape that merely touched a
 * neighbour at two far-apart corners - opposite ends of a long patch, say -
 * would swallow everything between them.
 */
export function conflateRing(
  ring: readonly L.LatLng[],
  map: L.Map,
  targets: readonly (readonly L.LatLng[])[],
  radiusPx: number = SnapRadiusPx,
): ConflateResult {
  if (ring.length < 3 || !targets.length) return { ring: [...ring], adopted: 0, seams: 0 };

  const located = ring.map(p => snapPoint(p, map, targets, radiusPx));
  const out: L.LatLng[] = [];
  let adopted = 0;
  let seams = 0;
  let inSeam = false;

  for (let i = 0; i < ring.length; i++) {
    const here = located[i];
    out.push(here ? here.latlng : ring[i]);

    const j = (i + 1) % ring.length;
    const next = located[j];
    const shares = here !== null && next !== null && here.ring === next.ring;
    if (!shares) { inSeam = false; continue; }

    const target = targets[here!.ring];
    const between = sharedRun(target, here!.at, next!.at);
    if (!between.length) {
      // Both ends on the same ring with nothing in between is still a shared
      // edge - a straight run of the neighbour - so the seam continues.
      if (!inSeam && here!.at !== next!.at) { seams++; inSeam = true; }
      continue;
    }

    // Does the drawn edge actually follow this neighbour, or does it only start
    // and end on it? Two ways of being the same border, and a run only has to
    // satisfy one.
    //
    // The midpoint test is the direct one: the drawn edge's middle is on the
    // neighbour, so the trace ran along it. On its own it was too strict, and
    // in exactly the place the gap appears. A trace alongside a border is
    // pulled onto it end-first; the straight edge left between two pulled
    // corners then cuts across wherever the border dips away, and the deeper
    // the dip the further its middle is from the ring - so the one case that
    // most needs the border's own vertices was the one refused, and the strip
    // of ground in the dip stayed in neither patch.
    //
    // So a detour that is barely longer than the straight line counts too.
    // That is what separates the two shapes this has to tell apart, and it is
    // not depth: a border wandering between two points stays close to the
    // direct path, while a bay - the inside of a C traced across its mouth -
    // is a long way round, and the ratio says so where a distance cannot.
    const mid = L.latLng((ring[i].lat + ring[j].lat) / 2, (ring[i].lng + ring[j].lng) / 2);
    const follows = snapPoint(mid, map, [target], radiusPx) !== null
      || detourRatio(here!.latlng, next!.latlng, between, map) <= MaxSeamDetour;
    if (!follows) { inSeam = false; continue; }

    out.push(...between);
    adopted += between.length;
    if (!inSeam) { seams++; inSeam = true; }
  }

  return { ring: dropAdjacentDuplicates(out), adopted, seams };
}

/**
 * How far the neighbour's own path between two shared points may wander before
 * it stops being the same border.
 *
 * A ratio, not a distance, because the thing being told apart is not a size.
 * A border that dips, kinks or wobbles between two points still gets there
 * about as directly as a straight line - call it 1.0 to 1.3. Going round the
 * inside of a bay to reach a point a short hop away is three or four times the
 * direct path, and stays that way however small the patch is. Two is clear of
 * both.
 */
export const MaxSeamDetour = 2;

/**
 * The neighbour's path from `from` to `to` through `run`, over the straight
 * line between them. Measured in pixels for the same reason everything else
 * here is: the ratio is scale-free, but the projection is not, and mixing
 * degrees of latitude with degrees of longitude would make the answer depend
 * on which way the border happened to run.
 */
function detourRatio(
  from: L.LatLng, to: L.LatLng, run: readonly L.LatLng[], map: L.Map,
): number {
  const at = (p: L.LatLng) => map.latLngToLayerPoint(p);
  const start = at(from);
  const end = at(to);
  const straight = start.distanceTo(end);
  // Two ends in the same place have no direct path to be a multiple of. The
  // run is a loop back to where it started, which is never a shared border.
  if (straight === 0) return Infinity;

  let walked = 0;
  let previous = start;
  for (const point of run) {
    const here = at(point);
    walked += previous.distanceTo(here);
    previous = here;
  }
  walked += previous.distanceTo(end);
  return walked / straight;
}

/**
 * The neighbour's own vertices strictly between two positions on it, walking
 * whichever way round is shorter.
 *
 * Positions are the fractional indices `snapPoint` reports. Endpoints are left
 * out because the caller already pushed the snapped points themselves; emitting
 * them again would put two handles on one corner.
 */
function sharedRun(ring: readonly L.LatLng[], from: number, to: number): L.LatLng[] {
  const n = ring.length;
  if (n < 2 || from === to) return [];

  const forward: L.LatLng[] = [];
  const end = to > from ? to : to + n;
  for (let k = Math.floor(from) + 1; k < end && forward.length < n; k++) {
    forward.push(ring[((k % n) + n) % n]);
  }

  const backward: L.LatLng[] = [];
  const floor = to < from ? to : to - n;
  for (let k = Math.ceil(from) - 1; k > floor && backward.length < n; k--) {
    backward.push(ring[((k % n) + n) % n]);
  }

  // Shorter in vertex count, which on a boundary is a good enough proxy for
  // shorter in distance and costs no projection.
  return backward.length < forward.length ? backward : forward;
}

/** Consecutive identical positions collapsed - a snapped vertex and an adopted
 *  one can be the same corner, and a ring may not repeat a point. */
function dropAdjacentDuplicates(ring: readonly L.LatLng[]): L.LatLng[] {
  const out: L.LatLng[] = [];
  for (const p of ring) {
    const last = out[out.length - 1];
    if (last && last.lat === p.lat && last.lng === p.lng) continue;
    out.push(p);
  }
  // The wrap-around pair too, or the ring closes onto its own first corner.
  while (out.length > 1) {
    const first = out[0];
    const last = out[out.length - 1];
    if (first.lat === last.lat && first.lng === last.lng) out.pop();
    else break;
  }
  return out;
}

/**
 * Rings of every territory except the one being edited, as snap targets.
 *
 * Its own ring is excluded on purpose: a vertex would snap to its own
 * neighbours the moment it was picked up and never move again.
 */
export function snapTargetsFrom(
  boundaries: readonly { id: number; rings: readonly (readonly L.LatLng[])[] }[],
  exceptId: number | null,
): readonly (readonly L.LatLng[])[] {
  return boundaries
    .filter(b => b.id !== exceptId)
    .flatMap(b => b.rings);
}

/** Ring area in square metres, for the readout while drawing. Spherical excess
 *  on the WGS84 sphere - close enough for a patch, and it costs no dependency. */
export function ringAreaSqMeters(ring: readonly L.LatLng[]): number {
  if (ring.length < 3) return 0;
  const R = 6378137;
  const rad = Math.PI / 180;
  let total = 0;
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i];
    const b = ring[(i + 1) % ring.length];
    total += (b.lng - a.lng) * rad *
      (2 + Math.sin(a.lat * rad) + Math.sin(b.lat * rad));
  }
  return Math.abs(total * R * R / 2);
}

/**
 * GeoJSON to Leaflet rings. Polygon gives its outer ring and any holes;
 * MultiPolygon is flattened, because a territory is often disjoint and every
 * part of it is equally editable.
 *
 * Returns empty on anything it cannot read rather than throwing: an unreadable
 * boundary must not take the page down, and the caller offers a redraw instead.
 */
export function ringsOf(boundaryGeoJson: string): L.LatLng[][] {
  try {
    const parsed = JSON.parse(boundaryGeoJson) as {
      type?: string;
      coordinates?: number[][][] | number[][][][];
    };
    const polygons: number[][][][] =
      parsed.type === 'Polygon' ? [parsed.coordinates as number[][][]]
      : parsed.type === 'MultiPolygon' ? (parsed.coordinates as number[][][][])
      : [];

    return polygons
      .flat()
      .map(ring => {
        // GeoJSON repeats the first position to close a ring; Leaflet closes
        // implicitly, and carrying the duplicate would put two handles on one
        // corner.
        const open = ring.length > 1 && samePosition(ring[0], ring[ring.length - 1])
          ? ring.slice(0, -1)
          : ring;
        return open.map(([lng, lat]) => L.latLng(lat, lng));
      })
      .filter(ring => ring.length >= 3);
  } catch {
    return [];
  }
}

/** Leaflet rings back to a GeoJSON Polygon, first position repeated to close. */
export function ringsToGeoJson(rings: readonly (readonly L.LatLng[])[]): string {
  const coordinates = rings.map(ring => {
    const positions = ring.map(p => [p.lng, p.lat]);
    positions.push([ring[0].lng, ring[0].lat]);
    return positions;
  });
  return JSON.stringify({ type: 'Polygon', coordinates });
}

function samePosition(a: number[], b: number[]): boolean {
  return a[0] === b[0] && a[1] === b[1];
}
